import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Which edge to fade:
 * - `bottom` (default): show the fade while content overflows below the fold and
 *   the user has not scrolled to the bottom. Attach `scrollRef` to the scroll
 *   element itself.
 * - `top`: show the fade while content overflows and the user has scrolled away
 *   from the top (i.e. content is tucked under a sticky header). Attach
 *   `scrollRef` to the sticky element; the hook walks up to the nearest
 *   scrollable ancestor, so it works even when the consumer does not own the
 *   scroll container (e.g. a sticky filter band inside `PageContentWrapper`).
 */
type ScrollFadeDirection = 'top' | 'bottom';

/**
 * Tracks whether a scroll container has content cropped past one edge so a
 * gradient fade can be shown over that edge and hidden once the user reaches it
 * (or when nothing overflows). Recomputes on scroll and on container resize.
 *
 * `onScroll` is returned for `bottom`-direction callers that wire it to a React
 * `onScroll` handler (the established pattern); the hook also listens natively,
 * so wiring it is optional.
 *
 * `externalElement` lets callers that don't own the scroll node (e.g. a
 * virtualized list whose scroller is rendered by react-window) drive the fade
 * off a DOM element they resolve imperatively. When provided, `scrollRef` is
 * ignored. Only the resolved element identity matters, so passing a freshly
 * read `.element` each render is fine — the effect re-runs when it changes.
 */
export const useScrollFade = <T extends HTMLElement = HTMLDivElement>(
  direction: ScrollFadeDirection = 'bottom',
  externalElement?: HTMLElement | null
) => {
  const scrollRef = useRef<T>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const [showFade, setShowFade] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const hasOverflow = el.scrollHeight > el.clientHeight;
    const atEdge =
      direction === 'bottom'
        ? el.scrollHeight - el.scrollTop - el.clientHeight < 1
        : el.scrollTop < 1;
    const next = hasOverflow && !atEdge;
    setShowFade((prev) => (prev === next ? prev : next));
  }, [direction]);

  useEffect(() => {
    const node = externalElement ?? scrollRef.current;
    if (!node) {
      return;
    }
    // `bottom` callers attach the ref to the scroll element itself. `top`
    // callers attach it to a sticky child, so resolve the nearest scrollable
    // ancestor that the sticky element pins within. An `externalElement` is
    // always the scroll element itself, so it skips the ancestor walk.
    let container: HTMLElement | null = node;
    if (direction === 'top' && !externalElement) {
      container = node.parentElement;
      while (container) {
        const overflowY = getComputedStyle(container).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          break;
        }
        container = container.parentElement;
      }
    }
    containerRef.current = container;
    if (!container) {
      return;
    }
    const target = container;
    checkOverflow();
    target.addEventListener('scroll', checkOverflow, { passive: true });
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(target);
    return () => {
      target.removeEventListener('scroll', checkOverflow);
      observer.disconnect();
    };
  }, [checkOverflow, direction, externalElement]);

  return { scrollRef, showFade, onScroll: checkOverflow };
};
